import React, { useState, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TableSortLabel,
  TextField,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import AddCustomer from '../components/AddCustomer';
import ConfirmationDialog from '../components/ConfirmationDialog';
import InfoModalV2 from '../components/InfoModalV2';
import { createCustomerSchema } from '../modals/CustomerSchema';
import useIsMobile from '../utils/useIsMobile';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  '& .MuiTableSortLabel-root': {
    color: `${theme.palette.common.white} !important`,
    '&.Mui-active': {
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiTableSortLabel-icon': {
      color: `${theme.palette.common.white} !important`,
    },
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.75) !important',
    },
  },
}));

function Customers({ setError, setSuccess }) {
  const { user, customers, userData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const isMobile = useIsMobile();

  // Local UI states for add/edit dialog
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Confirmation dialog state for deletion
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCustomer, setConfirmCustomer] = useState(null);

  // Pagination and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // New: State for the info modal (displaying full details)
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);

  // Dialog handlers for add/edit dialog
  const handleOpen = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setCustomerToEdit(null);
  };
  const handleAddCustomer = () => {
    handleCloseDialog();
  };

  // Toggle paperless
  const handleTogglePaperless = async (customer) => {
    if (!activeFarmId) {
      setError('No active farm selected.');
      return;
    }
    try {
      await updateDoc(doc(db, 'farms', activeFarmId, 'customers', customer.id), {
        paperless: !customer.paperless,
      });
      setSuccess(`Paperless setting updated for ${customer.businessName}`);
    } catch (error) {
      console.error("Error updating customer paperless status:", error);
      setError(`Could not update paperless setting for ${customer.businessName}`);
    }
  };

  // Toggle C.O.D.
  const handleToggleCod = async (customer) => {
    if (!activeFarmId) {
      setError('No active farm selected.');
      return;
    }
    try {
      await updateDoc(doc(db, 'farms', activeFarmId, 'customers', customer.id), {
        cod: !customer.cod,
      });
      setSuccess(`C.O.D. setting updated for ${customer.businessName}`);
    } catch (error) {
      console.error("Error updating customer C.O.D. status:", error);
      setError(`Could not update C.O.D. setting for ${customer.businessName}`);
    }
  };

  // Delete customer (called after confirmation)
  const handleDelete = async (id) => {
    if (!activeFarmId) {
      setError('No active farm selected.');
      return;
    }
    try {
      await deleteDoc(doc(db, 'farms', activeFarmId, 'customers', id));
      setSuccess('Customer deleted successfully');
    } catch (error) {
      console.error("Error deleting customer:", error);
      setError('Failed to delete customer');
    }
  };

  // Open confirmation dialog for deletion
  const openConfirmDialog = (customer) => {
    setConfirmCustomer(customer);
    setConfirmOpen(true);
  };

  // When user confirms deletion, call handleDelete
  const handleConfirmDelete = async () => {
    if (confirmCustomer) {
      await handleDelete(confirmCustomer.id);
    }
    setConfirmOpen(false);
    setConfirmCustomer(null);
  };

  // Cancel deletion
  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmCustomer(null);
  };

  const handleMenuOpen = (event, customer) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedCustomer(null);
  };
  const handleEdit = () => {
    setEditMode(true);
    setCustomerToEdit(selectedCustomer);
    handleOpen();
    handleMenuClose();
  };

  // Sort customers from Context
  const [customersSortDirection, setCustomersSortDirection] = useState('asc');
  const [customersSortColumn, setCustomersSortColumn] = useState('businessName');

  const handleCustomersSort = (column) => {
    const isAsc = customersSortColumn === column && customersSortDirection === 'asc';
    setCustomersSortDirection(isAsc ? 'desc' : 'asc');
    setCustomersSortColumn(column);
  };

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      if (a[customersSortColumn] < b[customersSortColumn]) {
        return customersSortDirection === 'asc' ? -1 : 1;
      }
      if (a[customersSortColumn] > b[customersSortColumn]) {
        return customersSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [customers, customersSortColumn, customersSortDirection]);

  // Filter customers by search term (searching businessName)
  const filteredCustomers = useMemo(() => {
    return sortedCustomers.filter((customer) =>
      customer.businessName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedCustomers, searchTerm]);

  // Paginate filtered customers
  const paginatedCustomers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredCustomers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCustomers, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const subscriptionActive = isSubscriptionActive(userData);

  // New: Row click handler to open the info modal
  const handleRowClick = (customer) => {
    setSelectedCustomerData(customer);
    setInfoModalOpen(true);
  };


  const customerSchema = useMemo(() => createCustomerSchema(), []);
  const displayOrNA = (value) => {
    const normalized = String(value ?? '').trim();
    return normalized ? normalized : 'N/A';
  };

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        maxWidth: { xs: '100%', sm: '100%', md: '80%'},
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ 
          maxWidth: { xs: 175, sm: 175, md: 300 }
          }}>
          <TextField
            label="Search by Business Name"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleOpen}
          disabled={!subscriptionActive}
        >
          Add Customer
        </Button>
      </Box>

      {isMobile && (
        <Paper sx={{ mb: 2 }}>
          <List disablePadding>
            {paginatedCustomers.map((customer) => (
              <React.Fragment key={customer.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleRowClick(customer)} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{displayOrNA(customer.businessName)}</Typography>
                          {customer.paperless && <Chip size="small" color="success" label="Paperless" />}
                          {customer.cod && <Chip size="small" color="warning" label="COD" />}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {displayOrNA(customer.pointOfcontact)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {displayOrNA(customer.contactNumber)} · {displayOrNA(customer.contactEmail)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {displayOrNA(customer.address)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
            {paginatedCustomers.length === 0 && (
              <ListItem>
                <ListItemText primary="No customers found." />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={customersSortColumn === 'businessName'}
                  direction={customersSortColumn === 'businessName' ? customersSortDirection : 'asc'}
                  onClick={() => handleCustomersSort('businessName')}
                >
                  Business Name
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={customersSortColumn === 'pointOfcontact'}
                  direction={customersSortColumn === 'pointOfcontact' ? customersSortDirection : 'asc'}
                  onClick={() => handleCustomersSort('pointOfcontact')}
                >
                  Point of Contact
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={customersSortColumn === 'address'}
                  direction={customersSortColumn === 'address' ? customersSortDirection : 'asc'}
                  onClick={() => handleCustomersSort('address')}
                >
                  Address
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={customersSortColumn === 'contactNumber'}
                  direction={customersSortColumn === 'contactNumber' ? customersSortDirection : 'asc'}
                  onClick={() => handleCustomersSort('contactNumber')}
                >
                  Contact Number
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={customersSortColumn === 'contactEmail'}
                  direction={customersSortColumn === 'contactEmail' ? customersSortDirection : 'asc'}
                  onClick={() => handleCustomersSort('contactEmail')}
                >
                  Contact Email
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Notes</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Mouse over note to read entire entry.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>Paperless</StyledTableCell>
              <StyledTableCell>Cash on Delivery</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCustomers.map((customer) => (
              // Make the row clickable; clicking (except on buttons) opens the info modal.
              <TableRow
                key={customer.id}
                onClick={() => handleRowClick(customer)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{displayOrNA(customer.businessName)}</TableCell>
                <TableCell>{displayOrNA(customer.pointOfcontact)}</TableCell>
                <TableCell>{displayOrNA(customer.address)}</TableCell>
                <TableCell>{displayOrNA(customer.contactNumber)}</TableCell>
                <TableCell>{displayOrNA(customer.contactEmail)}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip
                    title={<Typography sx={{ fontSize: '0.8rem' }}>{customer.notes || ''}</Typography>}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {customer.notes || ''}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    disabled={!subscriptionActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePaperless(customer);
                    }}
                  >
                    {customer.paperless ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                  </IconButton>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    disabled={!subscriptionActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleCod(customer);
                    }}
                  >
                    {customer.cod ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                  </IconButton>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton onClick={(e) => {
                    e.stopPropagation(); // This prevents the row's onClick from firing.
                    handleMenuOpen(e, customer);
                  }}>
                    <SettingsIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedCustomers.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No customers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredCustomers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem disabled={!subscriptionActive} onClick={handleEdit}>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openConfirmDialog(selectedCustomer);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <AddCustomer
        open={open}
        onClose={handleCloseDialog}
        onAdd={handleAddCustomer}
        editMode={editMode}
        customerToEdit={customerToEdit}
        setSuccess={setSuccess}
        setError={setError}
      />

      <ConfirmationDialog
        open={confirmOpen}
        title="Confirm Customer Deletion"
        message="Are you sure you want to delete this customer?"
        onConfirm={handleConfirmDelete}
        onClose={handleConfirmCancel}
      />

      {/* InfoModal for full customer details with defined fields */}
      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedCustomerData}
        schema={customerSchema}
      />
    </Box>
  );
}

export default Customers;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate ? userData.accessExpires.toDate() : new Date(userData.accessExpires);
  return now <= expires;
}


